package br.com.vvrefeicoes.portal.domain.model

enum class PurchasesType {
    PROTEINS,
    STAPLES,
    DAIRY,
    PRODUCE,
    CHINESE,
    CLEANING,
    DISPOSABLES,
}

data class PurchasesProductRow(
    val id: String,
    val name: String,
    val measurement: String,
    val consumption: Long,
    val unitPriceCents: Long,
    val totalCents: Long,
    val sortOrder: Int = 0,
)

data class PurchasesSummaryLine(
    val type: PurchasesType,
    val totalCents: Long,
    val productCount: Long,
)

data class PurchasesPanelCategory(
    val type: PurchasesType,
    val totalCents: Long,
    val previousTotalCents: Long,
    val productCount: Long,
    val totalReductionsCents: Long = 0L,
    val totalIncreasesCents: Long = 0L,
    val topReductions: List<PurchasesPanelProductUnitPriceChange> = emptyList(),
    val topIncreases: List<PurchasesPanelProductUnitPriceChange> = emptyList(),
    val monthly: List<PurchasesPanelCategoryMonth> = emptyList(),
    val details: List<PurchasesPanelProductDetail> = emptyList(),
) {
    val variationPercent: Double?
        get() =
            if (previousTotalCents == 0L) {
                null
            } else {
                (totalCents - previousTotalCents).toDouble() / previousTotalCents.toDouble() * 100.0
            }
}

data class PurchasesPanelCategoryMonth(
    val year: Int,
    val month: Int,
    val totalCents: Long,
    val variationPercent: Double?,
)

data class PurchasesPanelProductUnitPriceChange(
    val type: PurchasesType,
    val productName: String,
    val previousUnitPriceCents: Long,
    val currentUnitPriceCents: Long,
    val previousTotalCents: Long,
    val currentTotalCents: Long,
    val changeCents: Long,
    val variationPercent: Double,
    val totalChangeCents: Long,
    val totalVariationPercent: Double,
    val averageUnitPriceCents: Long = 0L,
    val monthly: List<PurchasesPanelProductMonthPrice> = emptyList(),
)

data class PurchasesPanelProductMonthPrice(
    val year: Int,
    val month: Int,
    val unitPriceCents: Long,
    val totalCents: Long = 0L,
    val changeCents: Long?,
    val variationPercent: Double?,
)

data class PurchasesPanelProductDetail(
    val type: PurchasesType,
    val productName: String,
    val previousUnitPriceCents: Long?,
    val currentUnitPriceCents: Long?,
    val changeCents: Long?,
    val variationPercent: Double?,
    val averageUnitPriceCents: Long,
    val monthly: List<PurchasesPanelProductMonthPrice>,
)

data class PurchasesPanelMonthlyCost(
    val month: Int,
    val totalCents: Long,
)

data class PurchasesPanelPeriodMonthlyCost(
    val year: Int,
    val month: Int,
    val totalCents: Long,
)

data class PurchasesPanelMonthlyCount(
    val month: Int,
    val orderCount: Long,
)

data class PurchasesPanelProductChange(
    val type: PurchasesType,
    val productName: String,
    val measurement: String,
    val previousUnitPriceCents: Long,
    val currentUnitPriceCents: Long,
    val variationPercent: Double,
)

data class PurchasesProvision(
    val year: Int,
    val month: Int,
    val amountCents: Long,
    val configured: Boolean,
)

data class PurchasesPanelData(
    val totalCents: Long,
    val previousTotalCents: Long,
    val plannedTotalCents: Long = 0L,
    val categories: List<PurchasesPanelCategory>,
    val extrasCount: Long,
    val previousExtrasCount: Long,
    val annualCosts: List<PurchasesPanelMonthlyCost> = emptyList(),
    val annualProvisions: List<PurchasesPanelMonthlyCost> = emptyList(),
    val periodProvisions: List<PurchasesPanelPeriodMonthlyCost> = emptyList(),
    val extrasEvolution: List<PurchasesPanelMonthlyCount> = emptyList(),
    val topIncreases: List<PurchasesPanelProductChange> = emptyList(),
    val topReductions: List<PurchasesPanelProductChange> = emptyList(),
    val extrasAnalytics: PurchasesExtraAnalytics = PurchasesExtraAnalytics(),
) {
    fun category(type: PurchasesType): PurchasesPanelCategory? = categories.firstOrNull { it.type == type }
}

data class PurchasesImportPayload(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val products: List<PurchasesImportProduct>,
)

data class PurchasesImportProduct(
    val name: String,
    val measurement: String,
    val consumption: String,
    val months: List<PurchasesImportMonth>,
)

data class PurchasesImportMonth(
    val month: Int,
    val unitPrice: String,
    val totalCost: String,
)

enum class PurchasesImportMode {
    FULL_YEAR,
    SELECTED_MONTH,
}

data class PurchasesImportPreview(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val mode: PurchasesImportMode,
    val yearHasEntries: Boolean,
    val productCount: Int,
    val rowCount: Int,
    val totalCents: Long,
    val canImport: Boolean,
)

data class PurchasesImportResult(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val mode: PurchasesImportMode,
    val monthsImported: Int,
    val rowsInserted: Int,
    val rowsReplaced: Int,
    val productCount: Int,
    val totalCents: Long,
)
